import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListFormSubmissionsResult,
} from '@whinerp/client';
import { Button, Num, SectionLabel } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';

/**
 * What a capture form collects, and what it received (F-036).
 *
 * Shown under the embed it belongs to, because the form IS the embed - the
 * origin allowlist and the token that make it safe to put in somebody else's
 * page are already on that row.
 *
 * The received counts include the REJECTIONS, and that is the point. A form
 * that silently drops spam is indistinguishable from a form nobody filled in,
 * and the workspace wondering why its enquiries stopped needs to see thirty
 * turned away rather than an empty list.
 */

const COLLECTABLE = [
  'first_name',
  'last_name',
  'email',
  'phone',
  'company_name',
  'title',
] as const;

export function CaptureFormPanel({ embedId }: { embedId: string }): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<string[]>(['first_name', 'email']);
  const [consent, setConsent] = useState(false);
  const [consentText, setConsentText] = useState('');
  const [doubleOptIn, setDoubleOptIn] = useState(false);
  const [saved, setSaved] = useState(false);

  const received = useQuery<ListFormSubmissionsResult, ApiError>({
    queryKey: ['form-submissions', embedId],
    queryFn: () => api.listFormSubmissions({ path: { embedId } }),
  });

  const save = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.saveCaptureForm({
        path: { embedId },
        body: {
          fields: fields.map((field) => ({
            field,
            label: field.replace(/_/g, ' '),
            required: field === 'email',
          })),
          consentRequired: consent,
          consentText: consent ? consentText.trim() : null,
          doubleOptIn: consent && doubleOptIn,
          honeypotField: 'website',
          minFillSeconds: 2,
        },
      }),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['form-submissions', embedId] });
    },
  });

  const counts = received.data?.counts;

  return (
    <div data-region="capture-form" data-form={embedId} className="flex flex-col gap-2 pt-2">
      <SectionLabel>{t('form.fields')}</SectionLabel>
      <p className="text-meta text-ink-meta">{t('form.fields.body')}</p>

      {save.error ? (
        <p data-form-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {save.error.message}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {COLLECTABLE.map((field) => (
          <label key={field} className="inline-flex items-center gap-1 text-meta text-ink">
            <input
              type="checkbox"
              data-form-field={field}
              checked={fields.includes(field)}
              onChange={(event) =>
                setFields((current) =>
                  event.target.checked
                    ? [...current, field]
                    : current.filter((entry) => entry !== field),
                )
              }
            />
            {field.replace(/_/g, ' ')}
          </label>
        ))}
      </div>

      <label className="inline-flex items-center gap-1 text-meta text-ink">
        <input
          type="checkbox"
          data-form-consent
          checked={consent}
          onChange={(event) => setConsent(event.target.checked)}
        />
        {t('form.consent')}
      </label>
      {consent ? (
        <input
          data-form-consent-text
          aria-label={t('form.consent.text')}
          placeholder={t('form.consent.text')}
          className="w-full rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
          value={consentText}
          onChange={(event) => setConsentText(event.target.value)}
        />
      ) : null}
      {/* Only with consent: double opt-in confirms a consent, so without one
          there is nothing to confirm (F-082). */}
      {consent ? (
        <label className="inline-flex items-center gap-1 text-meta text-ink">
          <input
            type="checkbox"
            data-form-double-opt-in
            checked={doubleOptIn}
            onChange={(event) => setDoubleOptIn(event.target.checked)}
          />
          {t('form.doubleOptIn')}
        </label>
      ) : null}

      <div className="flex items-center gap-2">
        <Button intent="secondary" size="small" data-form-save onClick={() => save.mutate()}>
          {t('form.save')}
        </Button>
        {saved ? (
          <span data-form-saved className="text-meta text-success-text">
            {t('form.saved')}
          </span>
        ) : null}
      </div>

      {/* Everything it received, rejections included. */}
      <SectionLabel>{t('form.received')}</SectionLabel>
      {counts === undefined || Object.values(counts).every((n) => n === 0) ? (
        <span data-form-received-none className="text-meta text-ink-meta">
          {t('form.received.none')}
        </span>
      ) : (
        <ul className="flex flex-wrap gap-3">
          <li data-form-received-created className="text-meta text-ink">
            <Num>{t('form.received.created', { values: { count: formatCount(counts.created, format) } })}</Num>
          </li>
          {counts.spam > 0 ? (
            <li data-form-received-spam className="text-meta text-warning-text">
              <Num>{t('form.received.spam', { values: { count: formatCount(counts.spam, format) } })}</Num>
            </li>
          ) : null}
          {counts['no-consent'] > 0 ? (
            <li data-form-received-no-consent className="text-meta text-warning-text">
              <Num>
                {t('form.received.noConsent', {
                  values: { count: formatCount(counts['no-consent'], format) },
                })}
              </Num>
            </li>
          ) : null}
          {counts.invalid > 0 ? (
            <li data-form-received-invalid className="text-meta text-warning-text">
              <Num>
                {t('form.received.invalid', { values: { count: formatCount(counts.invalid, format) } })}
              </Num>
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}
